import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gauge, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { login, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/");
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden flex-col justify-between bg-slate-900 p-10 lg:flex">
        <div className="flex items-center gap-2.5">
          <Gauge className="h-6 w-6 text-blue-400" strokeWidth={2.2} />
          <div className="leading-tight">
            <div className="font-display text-lg font-bold tracking-tight text-white">ImDash</div>
            <div className="font-display text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-400">
              Trade Intelligence
            </div>
          </div>
        </div>
        <div className="max-w-md">
          <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white">
            Welke auto's zijn nú de beste handelskansen voor import naar Nederland?
          </h2>
          <p className="mt-4 text-[13px] leading-relaxed text-slate-400">
            Deterministische BPM-berekening, Nederlandse marktwaardering op basis van echte
            vergelijkingsauto's, landed cost, handelsbijdrage, ROI en Deal Score — met volledige
            inzage in elke berekening.
          </p>
          <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-slate-800 pt-6">
            {[
              ["BPM-engine", "Forfaitaire tabel"],
              ["Waardering", "3 scenario's"],
              ["Deal Score", "0–100"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                  {k}
                </dt>
                <dd className="mono mt-1 text-[13px] text-slate-200">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <p className="text-[11px] text-slate-600">
          ImDash — toegang uitsluitend voor accounts met een actief abonnement.
        </p>
      </div>

      <div className="flex items-center justify-center bg-slate-50 p-6">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-2 lg:hidden">
            <Gauge className="h-5 w-5 text-blue-600" />
            <span className="font-display text-base font-bold tracking-tight text-slate-900">
              ImDash
            </span>
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
            Log in om verder te gaan
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">Beveiligde toegang tot ImDash.</p>

          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="login-form">
            <div>
              <label className="label-xs" htmlFor="email">
                E-mailadres
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError("");
                }}
                className="mt-1 h-9 w-full rounded-sm border border-slate-300 bg-white px-2.5 text-[13px] outline-none transition-colors duration-150 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
              />
            </div>
            <div>
              <label className="label-xs" htmlFor="password">
                Wachtwoord
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError("");
                }}
                className="mt-1 h-9 w-full rounded-sm border border-slate-300 bg-white px-2.5 text-[13px] outline-none transition-colors duration-150 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
              />
            </div>
            {error && (
              <div
                className="rounded-sm border border-red-300 bg-red-50 px-2.5 py-2 text-[12px] text-red-800"
                data-testid="login-error"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="flex h-9 w-full items-center justify-center gap-2 rounded-sm bg-blue-600 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700 disabled:opacity-60"
            >
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Inloggen
            </button>
            <Link
              to="/wachtwoord-vergeten"
              data-testid="forgot-password-link"
              className="block text-center text-[12px] text-blue-700 underline-offset-2 hover:underline"
            >
              Wachtwoord vergeten?
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
}
